/**
 * The review of a v2 reading, and the four endpoints around it (plan 4 §4,
 * §9, §10.1).
 *
 * **One truth: the generated contract.** Every type below is a name for a
 * `components["schemas"][…]` entry, and every call goes through the typed
 * `api.GET/POST` — nothing here restates a wire shape. What this file adds
 * is the one thing the contract cannot say: how a 409 or 422 of these
 * endpoints is sorted into what the screen can name.
 *
 * Coordinates are page fractions (0…1) of the image `extractionPageUrl(caseId,
 * 1)` serves; the proposal's are millimetres in the editor seed's frame.
 * Nothing here is a measurement except `valueMm`, `agreedMm`, the candidates'
 * `valueMm` and a proposal wall's asserted `lengthMm` — what the readings read
 * or a person set (A-014).
 */
import { ProblemError, toProblemDetails, type components } from "@k60/contracts";

import { api } from "./client";
import type { ExtractionEnqueuedOut, ExtractionOut } from "./extraction";

type Schemas = components["schemas"];

export type ReadingReviewOut = Schemas["ReadingReviewOut"];
export type ReviewWall = Schemas["ReviewWallOut"];
/** One reading of the form — `label` „A“, „B“ … stays with it, also where a chosen reading lists the chosen one first. */
export type FormCandidate = Schemas["FormCandidateOut"];
/** The one value that closes the ring (§10.1): a read figure, its comma moved. A suggestion — nothing is set. */
export type RingFix = Schemas["RingFixOut"];
/** A checked ring that misses on one axis — information, no value (E-16). */
export type RingHint = Schemas["RingHintOut"];
/** The form as the solver settles it — millimetres in the editor seed's frame (top left of the sheet is 0, 0). */
export type ReviewProposal = Schemas["ProposalOut"];
export type ProposalWall = Schemas["ProposalWallOut"];
/** `gewaehlt`: a person chose the form (plan 4 §10.1) — walls as on `geprueft`, never counted as the machine's check. */
export type FormStatus = ReadingReviewOut["formStatus"];
export type WallStatus = ReviewWall["status"];
export type PagePoint = [number, number];

export type Resolution = Schemas["ResolutionIn"];
export type ResolutionHow = Resolution["how"];
/** `ringfix` where the value is the ring's suggestion (§10.1) — a figure nobody read, never counted as a reading. */
export type ResolutionOrigin = NonNullable<Resolution["origin"]>;
export type TakeOverIn = Schemas["TakeOverIn"];
export type EditorSeedOut = Schemas["EditorSeedOut"];
export type SeenMark = Schemas["SeedWallOut"]["seen"];
export type FromEditorIn = Schemas["FromEditorIn"];
export type ChooseFormIn = Schemas["ChooseFormIn"];
/** What a follow-up job was asked — `choose_form` shows only „Maße lesen · Prüfen“ (plan 4 §9). */
export type ExtractionRequest = Schemas["ExtractionRequestOut"];
/**
 * The readings of the form a follow-up job was chosen from (plan 4 §9.1) —
 * on every such job, whatever its state, in the order of the reading of the
 * sheet („A“ first). A position in `candidates` is what `choose-form` takes
 * against `baseExtractionId`.
 */
export type FormChoice = Schemas["FormChoiceOut"];

/** The review inside a status answer — `null` for a v1 reading, which keeps its view. */
export function reviewOf(out: ExtractionOut): ReadingReviewOut | null {
  return out.review ?? null;
}

/** The follow-up a job was asked for, if any — `null` for a reading of the sheet. */
export function requestOf(out: ExtractionOut | undefined): ExtractionRequest | null {
  return out?.request ?? null;
}

/**
 * The readings a follow-up job was chosen from — from the server, so every
 * tab and device has them (review I-1). `null` for a reading of the sheet,
 * and where the server cannot show them.
 */
export function formChoiceOf(out: ExtractionOut | undefined): FormChoice | null {
  return out?.formChoice ?? null;
}

/**
 * A 409 on take-over, editor seed, from-editor or choose-form, sorted into
 * what the screen can say (plan 4 §4, B3; §9.1). The body is `ConflictOut
 * {detail, reasons, latestVersionNo}`; `reasons[0]` names it:
 * `stand_exists`, `reading_stale`, `form_not_checked`, `form_checked`.
 * Anything else is `unknown` — the screen shows the server's sentence and
 * offers to reload.
 */
export type ConflictKind = "stand_exists" | "stale" | "form_not_checked" | "form_checked" | "unknown";

export class TakeOverConflict extends Error {
  readonly name = "TakeOverConflict";

  constructor(
    readonly kind: ConflictKind,
    readonly detail: string | null,
  ) {
    super(detail ?? "Konflikt");
  }
}

/**
 * A 422: something the server would not take. On take-over (W3) the body is
 * `ConflictOut` with `reasons = [code, wallId]` — `duplicate_wall`,
 * `unknown_wall`, `value_out_of_range`, `unknown_how` — and a `detail` that
 * begins „W3 wurde nicht angenommen“. On choose-form it is `[unknown_candidate,
 * index]`, and `wallId` stays `null`. A body that fails the schema itself is
 * FastAPI's own, and names nothing.
 */
export class TakeOverRefused extends Error {
  readonly name = "TakeOverRefused";

  constructor(
    readonly wallId: string | null,
    readonly detail: string,
    readonly code: string | null = null,
  ) {
    super(detail);
  }
}

const KINDS: Record<string, ConflictKind> = {
  stand_exists: "stand_exists",
  reading_stale: "stale",
  form_not_checked: "form_not_checked",
  form_checked: "form_checked",
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function reasonsOf(payload: unknown): string[] {
  return isRecord(payload) && Array.isArray(payload.reasons)
    ? payload.reasons.filter((entry): entry is string => typeof entry === "string")
    : [];
}

export function conflictKind(payload: unknown): ConflictKind {
  const [first] = reasonsOf(payload);
  if (first !== undefined && first in KINDS) return KINDS[first]!;
  // A Stand without a reason word: the optimistic lock's own answer.
  if (isRecord(payload) && typeof payload.latestVersionNo === "number" && payload.latestVersionNo > 0) {
    return "stand_exists";
  }
  return "unknown";
}

function detailOf(payload: unknown): string | null {
  return isRecord(payload) && typeof payload.detail === "string" && payload.detail !== "" ? payload.detail : null;
}

/** 409 and 422 into the two errors above; anything else stays a `ProblemError`. */
function refusal(status: number, payload: unknown, names: "wall" | "candidate"): Error {
  if (status === 409) return new TakeOverConflict(conflictKind(payload), detailOf(payload));
  if (status === 422) {
    const [code, subject] = reasonsOf(payload);
    return new TakeOverRefused(
      names === "wall" ? (subject ?? null) : null,
      detailOf(payload) ?? "Die Eingaben entsprechen nicht der erwarteten Form.",
      code ?? null,
    );
  }
  return new ProblemError(toProblemDetails(payload, status), null);
}

/**
 * The typed client's two answer shapes, as everywhere in `api/`: a thrown
 * `ProblemError` from the shared middleware in the app, a resolved `{ data,
 * error, response }` where a test stubs `api.GET`/`api.POST`.
 */
async function answered<T>(
  request: () => Promise<{ data?: T; error?: unknown; response: Response }>,
  names: "wall" | "candidate" = "wall",
): Promise<T> {
  let answer: { data?: T; error?: unknown; response: Response };
  try {
    answer = await request();
  } catch (cause) {
    if (cause instanceof ProblemError) throw refusal(cause.status, cause.problem.raw, names);
    throw cause;
  }
  if (!answer.response.ok || answer.data === undefined) {
    throw refusal(answer.response.status, answer.error, names);
  }
  return answer.data;
}

/** `dryRun` answers the review as it would stand with these resolutions, and writes nothing. */
export function takeOver(caseId: string, input: TakeOverIn & { dryRun: true }): Promise<ReadingReviewOut>;
export function takeOver(caseId: string, input: TakeOverIn & { dryRun: false }): Promise<{ versionNo: number }>;
export async function takeOver(caseId: string, input: TakeOverIn): Promise<unknown> {
  return answered(() =>
    api.POST("/api/v1/cases/{case_id}/extraction/take-over", {
      params: { path: { case_id: caseId } },
      body: input,
    }),
  );
}

/**
 * „Diese Lesart nehmen — Maße lesen“ (plan 4 §9): a follow-up job reads the
 * measures of the form a person chose. Answered like `POST …/extraction` —
 * 202 queued, 200 the same choice in work or read before. `candidate` is the
 * position in the list the reading on screen shows (§9.1).
 */
export async function chooseForm(caseId: string, input: ChooseFormIn): Promise<ExtractionEnqueuedOut> {
  return answered(
    () =>
      api.POST("/api/v1/cases/{case_id}/extraction/choose-form", {
        params: { path: { case_id: caseId } },
        body: input,
      }),
    "candidate",
  );
}

export function editorSeedKey(caseId: string, extractionId: string, candidate: number) {
  return ["case", caseId, "extraction", "editor-seed", extractionId, candidate] as const;
}

export async function editorSeed(caseId: string, extractionId: string, candidate: number): Promise<EditorSeedOut> {
  return answered(() =>
    api.GET("/api/v1/cases/{case_id}/extraction/editor-seed", {
      params: { path: { case_id: caseId }, query: { extractionId, candidate } },
    }),
  );
}

export async function fromEditor(caseId: string, input: FromEditorIn): Promise<{ versionNo: number }> {
  return answered(() =>
    api.POST("/api/v1/cases/{case_id}/outline/from-editor", {
      params: { path: { case_id: caseId } },
      body: input,
    }),
  );
}
